'use client';

import { useTranslation } from '@weareconceptstudio/core';
import { forwardRef } from 'react';
import Link from 'next/link';

const DEFAULT_LOCALE = process.env.APP_DEFAULT_LOCALE;

const getPathWithoutLocale = (path, defaultLocale = DEFAULT_LOCALE) => {
	if (typeof path !== 'string') return path;
	const withoutDefault = path.replace(new RegExp(`^/${defaultLocale}(?=/|$)`), '') || '/';
	return withoutDefault;
};

const buildHref = (path, selectedLang, defaultLocale = DEFAULT_LOCALE) => {
	const pathWithoutLocale = getPathWithoutLocale(path, defaultLocale);
	if (selectedLang === defaultLocale) {
		return pathWithoutLocale;
	}
	return `/${selectedLang}${pathWithoutLocale}`;
};

const CustomLink = forwardRef(({ url, className = '', prefetch = true, scroll = true, replace = false, external = false, target, children, content, text, ariaLabel: ariaLabelProp, onMouseEnter = null, onMouseLeave = null, ...props }, ref) => {
	const { translate, selectedLang } = useTranslation();

	const href = typeof url === 'object'
		? { ...url, pathname: external ? url.pathname : buildHref(url.pathname, selectedLang) }
		: external ? url : buildHref(url, selectedLang);

	const ariaLabel = translate(text?.replaceAll('/', '') || ariaLabelProp || (typeof url === 'object' ? url.pathname?.replaceAll('/', '') : url?.replaceAll('/', '')));

	if (external) {
		return (
			<a
				ref={ref}
				href={href}
				className={`link-item ${className}`}
				rel='noopener noreferrer'
				target='_blank'
				aria-label={ariaLabel}
				onMouseEnter={onMouseEnter}
				onMouseLeave={onMouseLeave}
				{...props}
			>
				{children || (content ? translate(content) : null)}
			</a>
		);
	}

	return (
		<Link
			ref={ref}
			href={href}
			scroll={scroll}
			replace={replace}
			prefetch={prefetch}
			className={`link-item ${className}`}
			target={target || '_self'}
			aria-label={ariaLabel}
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			{...props}
		>
			{children || (content ? translate(content) : null)}
		</Link>
	);
});

export default CustomLink;
